import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getAdminComplexList, postRegisterBldg } from 'utils/api/admin';

const Estate = () => {
  const [complexList, setComplexList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const size = 20;
  const [url, setUrl] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchList();
  }, [page]);

  const fetchList = async (searchValue) => {
    try {
      const res = await getAdminComplexList({
        page,
        size,
        search: searchValue !== undefined ? searchValue : search,
      });
      setComplexList(res.results.items);
      setTotal(res.results.total);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchList(search);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setMessage('');
    setLoading(true);
    try {
      await postRegisterBldg(url.trim());
      setMessage('단지가 추가되었습니다.');
      setUrl('');
      fetchList();
    } catch (err) {
      setMessage('추가 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getNaverLink = (hscpNo) =>
    `https://m.land.naver.com/complex/info/${hscpNo}`;

  const totalPages = Math.ceil(total / size);

  return (
    <Container>
      <Title>단지 관리</Title>

      <FormCard>
        <SubTitle>단지 추가</SubTitle>
        <Form onSubmit={handleSubmit}>
          <FormRow>
            <Input
              placeholder="네이버 부동산 URL 입력 (예: https://m.land.naver.com/complex/info/111515)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              style={{ flex: 1 }}
            />
            <SubmitBtn type="submit" disabled={loading}>
              {loading ? '등록 중...' : '추가'}
            </SubmitBtn>
          </FormRow>
          {message && <Message>{message}</Message>}
        </Form>
      </FormCard>

      <ListHeader>
        <SubTitle style={{ margin: 0 }}>단지 목록 ({total}개)</SubTitle>
        <SearchForm onSubmit={handleSearch}>
          <SearchInput
            placeholder="단지명 또는 단지코드 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <SearchBtn type="submit">검색</SearchBtn>
        </SearchForm>
      </ListHeader>
      <Table>
        <thead>
          <tr>
            <Th>단지코드</Th>
            <Th>단지명</Th>
            <Th>세대수</Th>
            <Th>동수</Th>
            <Th>사용승인일</Th>
            <Th>면적</Th>
            <Th>바로가기</Th>
          </tr>
        </thead>
        <tbody>
          {complexList.map((c) => (
            <tr key={`${c.dong_code}_${c.hscp_no}`}>
              <Td>{c.hscp_no}</Td>
              <Td>{c.hscp_nm}</Td>
              <Td>{c.tot_hseh_cnt}</Td>
              <Td>{c.tot_dong_cnt}</Td>
              <Td>{c.use_aprv_ymd}</Td>
              <Td>
                {c.min_spc}~{c.max_spc}
              </Td>
              <Td>
                <LinkBtn
                  href={getNaverLink(c.hscp_no)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  네이버
                </LinkBtn>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page <= 1} onClick={() => setPage(page - 1)}>
            이전
          </PageBtn>
          <PageInfo>
            {page} / {totalPages}
          </PageInfo>
          <PageBtn
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </PageBtn>
        </Pagination>
      )}
    </Container>
  );
};

const Container = styled.div``;
const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #232d42;
  margin-bottom: 24px;
`;
const SubTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #232d42;
  margin-bottom: 16px;
`;
const FormCard = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
`;
const Form = styled.form``;
const FormRow = styled.div`
  display: flex;
  gap: 8px;
`;
const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  &:focus {
    border-color: #644ded;
  }
`;
const SubmitBtn = styled.button`
  padding: 10px 24px;
  background: #644ded;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #5338d4;
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;
const Message = styled.div`
  font-size: 13px;
  color: #38a169;
  margin-top: 8px;
`;
const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
const SearchForm = styled.form`
  display: flex;
  gap: 8px;
`;
const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  width: 250px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const SearchBtn = styled.button`
  padding: 8px 16px;
  background: #644ded;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #5338d4;
  }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;
const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  border-bottom: 1px solid #e4e4e4;
  background: #fafafa;
`;
const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #232d42;
  border-bottom: 1px solid #f0f0f0;
`;
const LinkBtn = styled.a`
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #644ded;
  color: #644ded;
  font-size: 12px;
  text-decoration: none;
  &:hover {
    background: #644ded;
    color: #fff;
  }
`;
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`;
const PageBtn = styled.button`
  padding: 6px 16px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
const PageInfo = styled.span`
  font-size: 13px;
  color: #8f9bba;
`;

export default Estate;
